import { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Link } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface FallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

const FallbackComponent = ({ error, componentStack, eventId, resetError }: FallbackProps) => {
  const handleReportClick = () => {
    if (eventId) {
      Sentry.showReportDialog({ eventId });
    }
  };

  const getErrorMessage = (err: unknown): string => {
    if (err instanceof Error) {
      return err.toString();
    }
    return JSON.stringify(err, null, 2);
  };

  const isDevelopment = import.meta.env.DEV;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 text-center">
        <div>
          <svg 
            className="mx-auto h-20 w-20 text-red-600" 
            fill="none" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Oups, une erreur s'est produite
          </h2>
          <p className="mt-2 text-gray-600">
            Nous avons rencontré un problème. L'erreur a été signalée à notre équipe technique.
          </p>
        </div>
        {isDevelopment && (
          <div className="mt-4 text-left p-4 bg-gray-100 rounded-lg overflow-auto">
            <h3 className="text-lg font-semibold text-red-600 mb-2">Détails de l'erreur (Dev uniquement)</h3>
            <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words">
              {error ? getErrorMessage(error) : 'Aucun détail disponible'}
            </pre>
            {componentStack && (
              <>
                <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">Component Stack:</h4>
                <pre className="text-sm text-gray-600 whitespace-pre-wrap break-words">
                  {componentStack}
                </pre>
              </>
            )}
          </div>
        )}
        <div className="mt-8 space-y-4">
          <button
            onClick={handleReportClick}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Signaler le problème
          </button>
          <Link
            to="/dashboard"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Retourner au tableau de bord
          </Link>
          <button
            onClick={resetError}
            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Réessayer
          </button>
        </div>
      </div>
    </div>
  );
};

// Use Sentry's built-in ErrorBoundary component
const SentryErrorBoundary = ({ children }: ErrorBoundaryProps) => (
  <Sentry.ErrorBoundary 
    fallback={FallbackComponent}
    showDialog={false}
    dialogOptions={{
      title: 'Une erreur inattendue s\'est produite',
      subtitle: 'Notre équipe a été notifiée',
      subtitle2: 'Si vous souhaitez nous aider, dites-nous ce qu\'il s\'est passé ci-dessous.',
      labelName: 'Nom',
      labelEmail: 'Email',
      labelComments: 'Que s\'est-il passé?',
      labelClose: 'Fermer',
      labelSubmit: 'Envoyer',
      errorGeneric: 'Une erreur inconnue s\'est produite lors de l\'envoi de votre rapport. Veuillez réessayer.',
      errorFormEntry: 'Certains champs ont été invalidés. Veuillez corriger les erreurs et réessayer.',
      successMessage: 'Votre feedback a été envoyé. Merci!'
    }}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default SentryErrorBoundary; 